/**
 * Feature 033 — Required Documents reference list (read-only, informational).
 *
 * This config drives the informational checklist rendered below the upload zone
 * on the Submit Claim page. Nothing here is bound to the form, validated, or sent
 * on the wire — it is a static lookup only. Lives beside providerConfig.ts
 * (feature-local by decision; promote to @claim-os/shared if a second feature
 * needs it).
 *
 * Keys are claim-type CODES (the same codes used by CLAIM_TYPE_PROVIDER_CONFIG).
 * Each value lists the EXTRA documents specific to that claim type — the three
 * documents common to every claim type live in GENERAL_DOCUMENTS and are shown
 * once in the always-visible "General" group, never repeated per type.
 */

/** Documents required for every claim type. Shown in the always-visible "General" group. */
export const GENERAL_DOCUMENTS: readonly string[] = [
  'Demographics',
  'Insurance',
  'Location of Services',
] as const;

/**
 * Per-claim-type EXTRA documents (beyond GENERAL_DOCUMENTS), keyed by claim-type code.
 * A code that is absent (or mapped to an empty array) contributes no extra rows.
 */
export const CLAIM_TYPE_REQUIRED_DOCUMENTS: Record<string, readonly string[]> = {
  OFFICE_VISIT: ['Superbill (or CPT & ICD 10 on the note)'],
  OFFICE_PROCEDURE: ['Procedure Note'],
  HOSPITAL_CONSULT: ['Consult note'],
  SURGICAL: ['OP Report'],
  // "Hospital Consult + Procedure Same Admission" rule maps to ER_CONSULT.
  ER_CONSULT: ['OP Report'],
  ANESTHESIA: ['Anesthesia Records'],
  PATHOLOGY: ['Superbill', 'Path Report'],
  NEUROMONITORING: ['Neuromonitoring Record'],
  FACILITY: ['OP Report'],
};

/**
 * Returns the extra documents for a claim-type code. Unknown codes return an
 * empty array so callers can render gracefully without guarding.
 */
export function getRequiredDocuments(claimType: string): readonly string[] {
  return CLAIM_TYPE_REQUIRED_DOCUMENTS[claimType] ?? [];
}
